import * as React from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/shared/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';

import type { DashboardQuote } from '../types';
import { normalizeQuoteStatus } from '../types';

interface AdminPendingQuotesTableProps {
  quotes: DashboardQuote[];
}

interface StatusMeta {
  label: string;
  variant: 'default' | 'warning' | 'muted';
}

const STATUS_META: Record<number, StatusMeta> = {
  2: { label: 'Under Review', variant: 'warning' },
  5: { label: 'Change Requested', variant: 'warning' },
};

function formatCurrency(value: number | undefined): string {
  // Guard undefined/null/NaN — but a legitimate $0 must still render as "$0",
  // not "N/A" (a plain `!value` check wrongly hides zero-value quotes).
  if (value === undefined || value === null || Number.isNaN(value)) return 'N/A';
  return `$${value.toLocaleString()}`;
}

/**
 * Pending-quotes table for the Operations Command Center.
 */
export function AdminPendingQuotesTable({ quotes }: AdminPendingQuotesTableProps) {
  if (quotes.length === 0) {
    return (
      <div className="tw-py-6 tw-text-center tw-text-sm tw-text-muted-foreground">
        No quotes pending approval.
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="tw-text-xs tw-uppercase tw-tracking-wide">Quote</TableHead>
          <TableHead className="tw-text-xs tw-uppercase tw-tracking-wide">Client</TableHead>
          <TableHead className="tw-text-xs tw-uppercase tw-tracking-wide">Status</TableHead>
          <TableHead className="tw-text-xs tw-uppercase tw-tracking-wide">Amount</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {quotes.map((quote) => {
          const status = normalizeQuoteStatus(quote.status);
          const meta = STATUS_META[status] ?? { label: 'Pending', variant: 'muted' as const };
          const amount = quote.totalAmount ?? quote.total;
          return (
            <TableRow key={quote.id}>
              <TableCell>
                <Link
                  to={`/quotes/view/${quote.id}`}
                  className="tw-font-medium tw-text-foreground hover:tw-text-primary"
                >
                  {quote.quoteNumber || `Q-${quote.id}`}
                </Link>
              </TableCell>
              <TableCell className="tw-text-muted-foreground">
                {quote.clientName || 'N/A'}
              </TableCell>
              <TableCell>
                <Badge variant={meta.variant}>{meta.label}</Badge>
              </TableCell>
              <TableCell className="tw-font-medium tw-text-foreground">
                {formatCurrency(amount)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
